/**
 * What fits my ride — Method-style localStorage + optional app proxy sync.
 */
; (function () {
  const EVENT_CHANGED = 'orp:vehicle-fit-changed'
  const EVENT_GRID = 'orp:product-grid-updated'

  function cfg() {
    return window.OrpVehicleFitConfig || {}
  }

  function str() {
    return window.OrpVehicleFitStrings || {}
  }

  function storageKey() {
    return cfg().storageKey || 'orp_customer_vehicle'
  }

  let memoryVehicle = null
  const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365

  function getCookie(name) {
    const escaped = String(name).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`))
    return match ? decodeURIComponent(match[1]) : null
  }

  function setCookie(name, value) {
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; samesite=lax`
  }

  function clearCookie(name) {
    document.cookie = `${name}=; path=/; max-age=0; samesite=lax`
  }

  const GENERATION_TABLE = {
    'Toyota|4Runner':      [{gen:'4th Gen',from:2003,to:2009},{gen:'5th Gen',from:2010,to:2024},{gen:'6th Gen',from:2025,to:2099}],
    'Toyota|Tacoma':       [{gen:'2nd Gen',from:2005,to:2015},{gen:'3rd Gen',from:2016,to:2023},{gen:'4th Gen',from:2024,to:2099}],
    'Toyota|Tundra':       [{gen:'1st Gen',from:2000,to:2006},{gen:'2nd Gen',from:2007,to:2021},{gen:'3rd Gen',from:2022,to:2099}],
    'Toyota|Sequoia':      [{gen:'1st Gen',from:2001,to:2007},{gen:'2nd Gen',from:2008,to:2022},{gen:'3rd Gen',from:2023,to:2099}],
    'Toyota|Land Cruiser': [{gen:'100 Series',from:2005,to:2007},{gen:'200 Series',from:2008,to:2021},{gen:'300 Series',from:2022,to:2099}],
    'Toyota|FJ Cruiser':   [{gen:'1st Gen',from:2007,to:2014}],
    'Lexus|GX 470':        [{gen:'1st Gen',from:2003,to:2009}],
    'Lexus|GX 460':        [{gen:'2nd Gen',from:2010,to:2023}],
    'Lexus|GX':            [{gen:'3rd Gen',from:2024,to:2099}],
    'Lexus|LX 470':        [{gen:'4th Gen',from:1998,to:2007}],
    'Lexus|LX 570':        [{gen:'5th Gen',from:2008,to:2021}],
    'Lexus|LX 600':        [{gen:'6th Gen',from:2022,to:2099}],
  }

  function lookupGeneration(year, make, model) {
    if (!year || !make || !model) return null
    const ranges = GENERATION_TABLE[`${make}|${model}`]
    if (!ranges) return null
    const y = parseInt(year, 10)
    const match = ranges.find(r => y >= r.from && y <= r.to)
    return match ? match.gen : null
  }

  function normalize(s) {
    if (s == null || s === '') return ''
    return String(s)
      .toLowerCase()
      .trim()
      .replace(/\s+/g, ' ')
  }

  function parseVehicle(raw) {
    if (!raw) return null
    try {
      const o = typeof raw === 'string' ? JSON.parse(raw) : raw
      if (!o || typeof o !== 'object') return null
      const year = o.year != null ? String(o.year).trim() : ''
      const make = o.make != null ? String(o.make).trim() : ''
      const model = o.model != null ? String(o.model).trim() : ''
      if (!year && !make && !model) return null
      const generation = o.generation != null ? String(o.generation).trim() : (lookupGeneration(year, make, model) || '')
      return { year, make, model, generation }
    } catch {
      return null
    }
  }

  function getStoredVehicleRaw() {
    const key = storageKey()
    try {
      const fromLocalStorage = localStorage.getItem(key)
      if (fromLocalStorage) return fromLocalStorage
      const fromCookie = getCookie(key)
      if (fromCookie) return fromCookie
      return memoryVehicle
    } catch {
      const fromCookie = getCookie(key)
      if (fromCookie) return fromCookie
      return memoryVehicle
    }
  }

  function setStoredVehicleRaw(raw) {
    const key = storageKey()
    try {
      if (!raw) {
        localStorage.removeItem(key)
        clearCookie(key)
        memoryVehicle = null
      } else {
        localStorage.setItem(key, raw)
        setCookie(key, raw)
        memoryVehicle = null
      }
    } catch {
      if (!raw) {
        clearCookie(key)
        memoryVehicle = null
      } else {
        setCookie(key, raw)
        memoryVehicle = raw
      }
    }
  }

  function getVehicle() {
    return parseVehicle(getStoredVehicleRaw())
  }

  function setVehicle(v) {
    const clean = parseVehicle(v)
    setStoredVehicleRaw(clean ? JSON.stringify(clean) : null)
    window.dispatchEvent(new CustomEvent(EVENT_CHANGED, { detail: { vehicle: clean } }))
  }

  function vehicleLabel(v) {
    if (!v) return ''
    const parts = [v.year, v.make, v.model].filter(Boolean)
    if (v.generation) parts.push(`(${v.generation})`)
    return parts.join(' ')
  }

  function vehicleLabelHtml(v) {
    if (!v) return ''
    const base = [v.year, v.make, v.model].filter(Boolean).map(escapeHtml).join(' ')
    if (!v.generation) return base
    return `${base} <span class="vf-gen-label">(${escapeHtml(v.generation)})</span>`
  }

  function asStringArray(v) {
    if (v == null) return []
    if (Array.isArray(v)) return v.map((x) => String(x)).filter(Boolean)
    if (typeof v === 'string' && v.trim()) return [v.trim()]
    return []
  }

  /**
   * @param {object} productData - from JSON script
   * @param {{year:string,make:string,model:string}} vehicle
   */
  function productFitsVehicle(productData, vehicle) {
    if (!vehicle || (!vehicle.year && !vehicle.make && !vehicle.model)) return false
    const ny = normalize(vehicle.year)
    const nm = normalize(vehicle.make)
    const nmo = normalize(vehicle.model)
    const tagPrefix = (cfg().tagPrefix || '').trim().toLowerCase()
    const tags = Array.isArray(productData.tags) ? productData.tags.map((t) => normalize(t)) : []

    if (tagPrefix && ny && nm && nmo) {
      const slug = [ny, nm.replace(/\s+/g, '-'), nmo.replace(/\s+/g, '-')].join('-')
      const combined = tagPrefix + slug
      if (tags.some((t) => t === normalize(combined) || t.includes(slug))) return true
    }

    const vehiclesRaw = productData.vehicles
    const vehicles = Array.isArray(vehiclesRaw)
      ? vehiclesRaw
      : Array.isArray(vehiclesRaw?.vehicles)
        ? vehiclesRaw.vehicles
        : []
    if (vehicles.length) {
      const matched = vehicles.some((v) => {
        const vMake = normalize(v.make || '')
        const vModel = normalize(v.model || '')
        const vYears = asStringArray(v.years).map(normalize)
        const makeOk = !nm || vMake === nm || vMake.includes(nm) || nm.includes(vMake)
        const modelOk = !nmo || vModel === nmo || vModel.includes(nmo) || nmo.includes(vModel)
        const yearOk = !ny || vYears.some((y) => y === ny || y.includes(ny) || ny.includes(y))
        return makeOk && modelOk && yearOk
      })
      if (matched) return true
    }

    const years = asStringArray(productData.years).map(normalize).filter(Boolean)
    const makes = asStringArray(productData.makes).map(normalize).filter(Boolean)
    const models = asStringArray(productData.models).map(normalize).filter(Boolean)
    const raw = normalize(productData.fitmentsRaw || '')
    if (!raw && years.length && makes.length && models.length) {
      const yOk = !ny || years.some((y) => y === ny || y.includes(ny) || ny.includes(y))
      const mOk = !nm || makes.some((m) => m === nm || m.includes(nm) || nm.includes(m))
      const moOk = !nmo || models.some((m) => m === nmo || m.includes(nmo) || nmo.includes(m))
      if (yOk && mOk && moOk) return true
    }

    const fitmentTokens = asStringArray(productData.fitmentTokens).map(normalize).filter(Boolean)
    if (fitmentTokens.length && ny && nm && nmo) {
      const triple = `${ny} ${nm} ${nmo}`
      if (fitmentTokens.some((t) => t.includes(triple) || triple.includes(t))) return true
      if (
        fitmentTokens.some(
          (t) =>
            (!ny || t.includes(ny)) && (!nm || t.includes(nm)) && (!nmo || t.includes(nmo)),
        )
      )
        return true
    }

    if (raw && ny && nm && nmo) {
      const delim = cfg().fitmentDelimiter || '|'
      const parts = raw.split(delim).map((p) => normalize(p)).filter(Boolean)
      const triple = `${ny} ${nm} ${nmo}`
      if (parts.some((p) => p.includes(triple) || triple.includes(p))) return true
      if (
        parts.some(
          (p) =>
            (!ny || p.includes(ny)) && (!nm || p.includes(nm)) && (!nmo || p.includes(nmo)),
        )
      )
        return true
    }

    return false
  }

  async function fetchJson(url, options = {}) {
    const res = await fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...options.headers },
      ...options,
    })
    if (!res.ok) throw new Error(String(res.status))
    return res.json()
  }

  async function loadYmmLevel(level, { year = '', make = '' } = {}) {
    const prefix = (cfg().proxyPrefix || '/apps/vehicle-fit').replace(/\/$/, '')
    const params = new URLSearchParams({ level, year, make })
    try {
      const data = await fetchJson(`${prefix}/options?${params}`)
      if (data && Array.isArray(data.values)) return data.values
    } catch {
      /* fall back */
    }
    const fallbackUrl = cfg().ymmFallbackUrl
    if (!fallbackUrl) return []
    try {
      const tree = await fetchJson(fallbackUrl)
      if (level === 'year') return tree.years || []
      if (level === 'make') return (tree.makes && tree.makes[year]) || []
      if (level === 'model') {
        const k = `${year}|${make}`
        return (tree.models && tree.models[k]) || []
      }
    } catch {
      return []
    }
    return []
  }

  async function postProxy(path, body) {
    const prefix = (cfg().proxyPrefix || '/apps/vehicle-fit').replace(/\/$/, '')
    return fetchJson(`${prefix}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {}),
    })
  }

  function mergeInitialVehicle() {
    const local = getVehicle()
    const server = cfg().initialFromServer
    if (!server || typeof server !== 'object') return local
    const sy = server.year != null ? String(server.year).trim() : ''
    const sm = server.make != null ? String(server.make).trim() : ''
    const smo = server.model != null ? String(server.model).trim() : ''
    if (!sy && !sm && !smo) return local
    const generation = server.generation != null ? String(server.generation).trim() : (lookupGeneration(sy, sm, smo) || '')
    const serverV = { year: sy, make: sm, model: smo, generation }
    setStoredVehicleRaw(JSON.stringify(serverV))
    return serverV
  }

  const BOOTSTRAP_ICONS_CSS =
    'https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css'

  /**
   * Icon font for .bi must load inside the shadow root (no inherited document styles).
   */
  function ensureBootstrapIconsInShadow(shadowRoot) {
    if (!shadowRoot || shadowRoot.querySelector('link[data-orp-bootstrap-icons]')) return
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = BOOTSTRAP_ICONS_CSS
    link.setAttribute('data-orp-bootstrap-icons', '')
    shadowRoot.insertBefore(link, shadowRoot.firstChild)
  }

  /** Bootstrap icon suffix only: letters, digits, hyphen (e.g. check-lg). */
  function sanitizeBadgeIconName(raw) {
    if (raw == null || raw === '') return ''
    const s = String(raw).trim().toLowerCase()
    if (!/^[a-z0-9-]+$/.test(s)) return ''
    return s
  }

  function removeVehicleFitBadgesFromDom() {
    document.querySelectorAll('nova-product-card').forEach((card) => {
      const root = card.shadowRoot
      if (!root) return
      root.querySelectorAll('.vehicle-fit-compat-badge').forEach((el) => el.remove())
      root.querySelectorAll('.product-card-vf-badge-wrap').forEach((el) => el.remove())
    })
  }

  function refreshProductBadges() {
    if (!cfg().badgesEnabled) {
      removeVehicleFitBadgesFromDom()
      return
    }
    const vehicle = getVehicle()
    document
      .querySelectorAll('nova-product-card[data-vehicle-fit-product-id]')
      .forEach((card) => {
        const id = card.getAttribute('data-vehicle-fit-product-id')
        const script = id && document.getElementById(`vehicle-fit-data-${id}`)
        if (!script) return
        let data = {}
        try {
          data = JSON.parse(script.textContent.trim() || '{}')
        } catch {
          data = {}
        }

        const badge = ensureVehicleFitBadge(card)
        if (!badge) return

        const fits = vehicle && productFitsVehicle(data, vehicle)
        badge.hidden = !fits
      })
  }

  function ensureVehicleFitBadge(card) {
    const root = card.shadowRoot
    if (!root) return null

    const media = root.querySelector('.product-card__media')
    if (!media) return null

    // Find the .badges column managed by the product card (product metafield badges).
    // If it exists, stack the VF badge inside it. If not, create our own wrapper so
    // both badges share one absolutely-positioned flex column.
    let badgesContainer = root.querySelector('.badges')
    if (!badgesContainer) {
      badgesContainer = media.querySelector('.product-card-vf-badge-wrap')
      if (!badgesContainer) {
        badgesContainer = document.createElement('div')
        badgesContainer.className = 'product-card-vf-badge-wrap'
        badgesContainer.style.position = 'absolute'
        badgesContainer.style.top = '8px'
        badgesContainer.style.left = '8px'
        badgesContainer.style.zIndex = '6'
        badgesContainer.style.display = 'flex'
        badgesContainer.style.flexDirection = 'column'
        badgesContainer.style.gap = '5px'
        badgesContainer.style.pointerEvents = 'none'
        media.appendChild(badgesContainer)
      }
    }

    let badge = root.querySelector('.vehicle-fit-compat-badge')
    if (!badge) {
      badge = document.createElement('span')
      badge.className = 'vehicle-fit-compat-badge'
      badge.setAttribute('role', 'status')
      badge.style.pointerEvents = 'none'
      badge.style.display = 'inline-block'
      badge.style.borderRadius = '4px'
      badge.style.padding = '0.35rem 0.45rem'
      badge.style.textTransform = 'uppercase'
      badge.style.letterSpacing = '0.03em'
      badge.style.lineHeight = '1.2'
      badge.style.border = 'none'
      badge.style.fontFamily = 'Rubik, sans-serif'
      badgesContainer.insertBefore(badge, badgesContainer.firstChild)
    } else if (badge.parentNode !== badgesContainer) {
      // Badge was created before Lit rendered .badges — move it to the right container
      const oldParent = badge.parentNode
      badgesContainer.insertBefore(badge, badgesContainer.firstChild)
      if (oldParent?.classList.contains('product-card-vf-badge-wrap') && !oldParent.children.length) {
        oldParent.remove()
      }
    }

    const rawLabel = cfg().badgeLabel
    const customLabel = rawLabel != null ? String(rawLabel).trim() : ''
    const labelText =
      customLabel || str().badge_compatible || 'Compatible with your vehicle'
    const iconName = sanitizeBadgeIconName(cfg().badgeIconName)

    badge.replaceChildren()
    if (iconName) {
      ensureBootstrapIconsInShadow(root)
      badge.style.display = 'inline-flex'
      badge.style.alignItems = 'center'
      badge.style.gap = '0.35em'
      const iconEl = document.createElement('i')
      iconEl.className = `bi bi-${iconName}`
      iconEl.setAttribute('aria-hidden', 'true')
      iconEl.style.display = 'inline-flex'
      iconEl.style.alignItems = 'center'
      badge.appendChild(iconEl)
    } else {
      badge.style.display = 'inline-block'
      badge.style.alignItems = ''
      badge.style.gap = ''
    }
    const textEl = document.createElement('span')
    textEl.textContent = labelText
    badge.appendChild(textEl)

    const bg = cfg().badgeBackgroundColor
    const fg = cfg().badgeTextColor
    if (bg) badge.style.background = String(bg).trim()
    if (fg) badge.style.color = String(fg).trim()
    const fs = cfg().badgeFontSize
    if (fs != null && String(fs).trim() !== '') {
      const n = String(fs).trim().replace(/px$/i, '')
      badge.style.fontSize = `${n}px`
    }
    const fw = cfg().badgeFontWeight
    if (fw != null && String(fw).trim() !== '') {
      badge.style.fontWeight = String(fw).trim()
    }
    return badge
  }

  function updateVehicleFitBar(bar) {
    if (!bar) return
    const vehicle = getVehicle()
    const label = bar.querySelector('[data-vehicle-fit-bar-label]')
    const vehicleEl = bar.querySelector('[data-vehicle-fit-bar-vehicle]')
    if (!vehicle || (!vehicle.year && !vehicle.make && !vehicle.model)) {
      bar.classList.remove('is-visible')
      bar.hidden = true
      return
    }
    const productId = bar.getAttribute('data-vehicle-fit-product-id')
    if (productId) {
      const script = document.getElementById(`vehicle-fit-data-${productId}`)
      if (!script) {
        bar.classList.remove('is-visible')
        bar.hidden = true
        return
      }
      let data = {}
      try {
        data = JSON.parse(script.textContent.trim() || '{}')
      } catch {
        data = {}
      }
      const fits = productFitsVehicle(data, vehicle)
      if (!fits) {
        bar.classList.remove('is-visible')
        bar.hidden = true
        return
      }
    }
    bar.hidden = false
    bar.classList.add('is-visible')
    if (label) {
      const customTitle = cfg().collectionBarTitle
      const t = customTitle != null ? String(customTitle).trim() : ''
      label.textContent = t || str().bar_verified || 'Vehicle verified'
    }
    if (vehicleEl) {
      vehicleEl.innerHTML = vehicleLabelHtml(vehicle)
      vehicleEl.setAttribute('aria-label', vehicleLabel(vehicle))
    }
  }

  function updateCollectionBar() {
    if (cfg().showCollectionBar) {
      document.querySelectorAll('[data-vehicle-fit-collection-bar]').forEach(updateVehicleFitBar)
    }
    document.querySelectorAll('[data-vehicle-fit-product-page-bar]').forEach(updateVehicleFitBar)
  }

  let modalEl = null
  let lastFocus = null

  function closeModal() {
    const overlay = document.querySelector('[data-vehicle-fit-modal]')
    if (!overlay) return
    overlay.classList.remove('is-open')
    overlay.setAttribute('aria-hidden', 'true')
    document.body.style.overflow = ''
    if (lastFocus) {
      try {
        lastFocus.focus()
      } catch {
        /* ignore */
      }
      lastFocus = null
    }
  }

  async function openModal() {
    if (cfg().showModal === false) return
    const overlay = document.querySelector('[data-vehicle-fit-modal]')
    if (!overlay) return
    lastFocus = document.activeElement
    overlay.classList.add('is-open')
    overlay.setAttribute('aria-hidden', 'false')
    document.body.style.overflow = 'hidden'
    await hydrateModalSelects()
    const y = overlay.querySelector('[data-vehicle-fit-year]')
    y?.focus()
  }

  async function hydrateModalSelects() {
    const overlay = document.querySelector('[data-vehicle-fit-modal]')
    if (!overlay) return
    const selY = overlay.querySelector('[data-vehicle-fit-year]')
    const selM = overlay.querySelector('[data-vehicle-fit-make]')
    const selMo = overlay.querySelector('[data-vehicle-fit-model]')
    const current = getVehicle()

    const years = await loadYmmLevel('year')
    if (selY) {
      const yv = current?.year || ''
      selY.innerHTML =
        `<option value="">${str().year_placeholder || 'Year'}</option>` +
        years.map((y) => `<option value="${escapeAttr(y)}"${y === yv ? ' selected' : ''}>${escapeHtml(y)}</option>`).join('')
    }

    if (selM) {
      selM.innerHTML = `<option value="">${str().make_placeholder || 'Make'}</option>`
      selM.disabled = !current?.year
      if (current?.year) {
        const makes = await loadYmmLevel('make', { year: current.year })
        const mv = current?.make || ''
        selM.innerHTML +=
          makes.map((m) => `<option value="${escapeAttr(m)}"${m === mv ? ' selected' : ''}>${escapeHtml(m)}</option>`).join('')
      }
    }

    if (selMo) {
      selMo.innerHTML = `<option value="">${str().model_placeholder || 'Model'}</option>`
      selMo.disabled = !current?.year || !current?.make
      if (current?.year && current?.make) {
        const models = await loadYmmLevel('model', { year: current.year, make: current.make })
        const modv = current?.model || ''
        selMo.innerHTML +=
          models.map((m) => `<option value="${escapeAttr(m)}"${m === modv ? ' selected' : ''}>${escapeHtml(m)}</option>`).join('')
      }
    }
    updateModalGenDisplay()
  }

  function updateModalGenDisplay() {
    const overlay = document.querySelector('[data-vehicle-fit-modal]')
    if (!overlay) return
    const genEl = overlay.querySelector('[data-vehicle-fit-gen]')
    if (!genEl) return
    const year  = overlay.querySelector('[data-vehicle-fit-year]')?.value  || ''
    const make  = overlay.querySelector('[data-vehicle-fit-make]')?.value  || ''
    const model = overlay.querySelector('[data-vehicle-fit-model]')?.value || ''
    const gen = lookupGeneration(year, make, model)
    if (gen) {
      genEl.textContent = gen
      genEl.hidden = false
    } else {
      genEl.textContent = ''
      genEl.hidden = true
    }
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  }

  function escapeAttr(s) {
    return escapeHtml(s).replace(/'/g, '&#39;')
  }

  async function onYearChange() {
    const overlay = document.querySelector('[data-vehicle-fit-modal]')
    const selY = overlay?.querySelector('[data-vehicle-fit-year]')
    const selM = overlay?.querySelector('[data-vehicle-fit-make]')
    const selMo = overlay?.querySelector('[data-vehicle-fit-model]')
    const year = selY?.value || ''
    if (selM) {
      selM.innerHTML = `<option value="">${str().make_placeholder || 'Make'}</option>`
      selM.disabled = !year
      if (year) {
        const makes = await loadYmmLevel('make', { year })
        selM.innerHTML += makes.map((m) => `<option value="${escapeAttr(m)}">${escapeHtml(m)}</option>`).join('')
      }
    }
    if (selMo) {
      selMo.innerHTML = `<option value="">${str().model_placeholder || 'Model'}</option>`
      selMo.disabled = true
    }
    updateModalGenDisplay()
  }

  async function onMakeChange() {
    const overlay = document.querySelector('[data-vehicle-fit-modal]')
    const selY = overlay?.querySelector('[data-vehicle-fit-year]')
    const selM = overlay?.querySelector('[data-vehicle-fit-make]')
    const selMo = overlay?.querySelector('[data-vehicle-fit-model]')
    const year = selY?.value || ''
    const make = selM?.value || ''
    if (selMo) {
      selMo.innerHTML = `<option value="">${str().model_placeholder || 'Model'}</option>`
      selMo.disabled = !year || !make
      if (year && make) {
        const models = await loadYmmLevel('model', { year, make })
        selMo.innerHTML += models.map((m) => `<option value="${escapeAttr(m)}">${escapeHtml(m)}</option>`).join('')
      }
    }
    updateModalGenDisplay()
  }

  async function saveModal() {
    const overlay = document.querySelector('[data-vehicle-fit-modal]')
    const selY = overlay?.querySelector('[data-vehicle-fit-year]')
    const selM = overlay?.querySelector('[data-vehicle-fit-make]')
    const selMo = overlay?.querySelector('[data-vehicle-fit-model]')
    const year = selY?.value?.trim() || ''
    const make = selM?.value?.trim() || ''
    const model = selMo?.value?.trim() || ''
    if (!year || !make || !model) return
    const vehicle = { year, make, model, generation: lookupGeneration(year, make, model) || '' }
    setVehicle(vehicle)
    closeModal()
    updateCollectionBar()
    refreshProductBadges()
    hydrateAccountSelects()
    if (cfg().customerLoggedIn) {
      postProxy('/save', vehicle).catch(() => {
        /* local still saved */
      })
    }
  }

  async function clearVehicleEverywhere() {
    setStoredVehicleRaw(null)
    if (cfg().customerLoggedIn) {
      try {
        await postProxy('/clear', {})
      } catch {
        /* ignore */
      }
    }
    window.dispatchEvent(new CustomEvent(EVENT_CHANGED, { detail: { vehicle: null } }))
    updateCollectionBar()
    refreshProductBadges()
    closeModal()
    hydrateAccountSelects()
  }

  function bindModal() {
    modalEl = document.querySelector('[data-vehicle-fit-modal]')
    if (!modalEl) return
    modalEl.addEventListener('click', (e) => {
      if (e.target === modalEl) closeModal()
    })
    modalEl.querySelector('[data-vehicle-fit-close]')?.addEventListener('click', closeModal)
    modalEl.querySelector('[data-vehicle-fit-save]')?.addEventListener('click', () => saveModal())
    modalEl.querySelector('[data-vehicle-fit-clear]')?.addEventListener('click', () => clearVehicleEverywhere())
    modalEl.querySelector('[data-vehicle-fit-year]')?.addEventListener('change', () => onYearChange())
    modalEl.querySelector('[data-vehicle-fit-make]')?.addEventListener('change', () => onMakeChange())
    modalEl.querySelector('[data-vehicle-fit-model]')?.addEventListener('change', () => updateModalGenDisplay())
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modalEl?.classList.contains('is-open')) closeModal()
    })
  }

  function bindTriggers() {
    document.body.addEventListener('click', (e) => {
      const t = e.target.closest('[data-vehicle-fit-open]')
      if (t) {
        e.preventDefault()
        openModal()
      }
    })
  }

  function closeVehicleFitProductDropdowns(bar) {
    bar?.querySelectorAll('sl-dropdown').forEach((dd) => {
      if (typeof dd.hide === 'function') dd.hide()
      else dd.open = false
    })
  }

  function bindVehicleFitBar(bar) {
    if (!bar) return
    bar.querySelector('[data-vehicle-fit-bar-vehicle]')?.addEventListener('click', (e) => {
      e.preventDefault()
      openModal()
    })
    bar.querySelectorAll('[data-vehicle-fit-bar-change]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault()
        closeVehicleFitProductDropdowns(bar)
        openModal()
      })
    })
    bar.querySelectorAll('[data-vehicle-fit-bar-remove]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault()
        closeVehicleFitProductDropdowns(bar)
        clearVehicleEverywhere()
      })
    })
  }

  function bindCollectionBar() {
    document.querySelectorAll('[data-vehicle-fit-collection-bar]').forEach(bindVehicleFitBar)
    document.querySelectorAll('[data-vehicle-fit-product-page-bar]').forEach(bindVehicleFitBar)
  }

  function updateHeaderButton() {
    const btn = document.querySelector('[data-vehicle-fit-header-btn]')
    if (!btn) return
    const label = btn.querySelector('[data-vehicle-fit-header-label]')
    if (!label) return
    const vehicle = getVehicle()
    if (vehicle && (vehicle.year || vehicle.model)) {
      const yr = vehicle.year ? `'${String(vehicle.year).slice(-2)}` : ''
      label.textContent = [yr, vehicle.model].filter(Boolean).join(' ')
      btn.setAttribute('aria-label', [vehicle.year, vehicle.model].filter(Boolean).join(' '))
    } else {
      label.textContent = btn.getAttribute('data-vehicle-fit-default-label') || ''
      btn.removeAttribute('aria-label')
    }
  }

  function setAccentColor() {
    const hex = cfg().accentColor
    if (hex) document.documentElement.style.setProperty('--vehicle-fit-accent', hex)
  }

  async function hydrateAccountSelects() {
    const root = document.querySelector('[data-vehicle-fit-account]')
    if (!root || !cfg().enabled) return
    const selY = root.querySelector('[data-account-vehicle-year]')
    const selM = root.querySelector('[data-account-vehicle-make]')
    const selMo = root.querySelector('[data-account-vehicle-model]')
    const current = getVehicle()
    const years = await loadYmmLevel('year')
    if (selY) {
      const yv = current?.year || ''
      selY.innerHTML =
        `<option value="">${str().year_placeholder || 'Year'}</option>` +
        years.map((y) => `<option value="${escapeAttr(y)}"${y === yv ? ' selected' : ''}>${escapeHtml(y)}</option>`).join('')
    }
    if (selM) {
      selM.innerHTML = `<option value="">${str().make_placeholder || 'Make'}</option>`
      selM.disabled = !current?.year
      if (current?.year) {
        const makes = await loadYmmLevel('make', { year: current.year })
        const mv = current?.make || ''
        selM.innerHTML +=
          makes.map((m) => `<option value="${escapeAttr(m)}"${m === mv ? ' selected' : ''}>${escapeHtml(m)}</option>`).join('')
      }
    }
    if (selMo) {
      selMo.innerHTML = `<option value="">${str().model_placeholder || 'Model'}</option>`
      selMo.disabled = !current?.year || !current?.make
      if (current?.year && current?.make) {
        const models = await loadYmmLevel('model', { year: current.year, make: current.make })
        const modv = current?.model || ''
        selMo.innerHTML +=
          models.map((m) => `<option value="${escapeAttr(m)}"${m === modv ? ' selected' : ''}>${escapeHtml(m)}</option>`).join('')
      }
    }
  }

  async function onAccountYearChange() {
    const root = document.querySelector('[data-vehicle-fit-account]')
    const selY = root?.querySelector('[data-account-vehicle-year]')
    const selM = root?.querySelector('[data-account-vehicle-make]')
    const selMo = root?.querySelector('[data-account-vehicle-model]')
    const year = selY?.value || ''
    if (selM) {
      selM.innerHTML = `<option value="">${str().make_placeholder || 'Make'}</option>`
      selM.disabled = !year
      if (year) {
        const makes = await loadYmmLevel('make', { year })
        selM.innerHTML += makes.map((m) => `<option value="${escapeAttr(m)}">${escapeHtml(m)}</option>`).join('')
      }
    }
    if (selMo) {
      selMo.innerHTML = `<option value="">${str().model_placeholder || 'Model'}</option>`
      selMo.disabled = true
    }
  }

  async function onAccountMakeChange() {
    const root = document.querySelector('[data-vehicle-fit-account]')
    const selY = root?.querySelector('[data-account-vehicle-year]')
    const selM = root?.querySelector('[data-account-vehicle-make]')
    const selMo = root?.querySelector('[data-account-vehicle-model]')
    const year = selY?.value || ''
    const make = selM?.value || ''
    if (selMo) {
      selMo.innerHTML = `<option value="">${str().model_placeholder || 'Model'}</option>`
      selMo.disabled = !year || !make
      if (year && make) {
        const models = await loadYmmLevel('model', { year, make })
        selMo.innerHTML += models.map((m) => `<option value="${escapeAttr(m)}">${escapeHtml(m)}</option>`).join('')
      }
    }
  }

  function setAccountStatus(message, isError) {
    const el = document.querySelector('[data-account-vehicle-status]')
    if (!el) return
    el.textContent = message || ''
    el.classList.toggle('is-error', Boolean(isError))
  }

  async function saveAccountVehicle() {
    const root = document.querySelector('[data-vehicle-fit-account]')
    const selY = root?.querySelector('[data-account-vehicle-year]')
    const selM = root?.querySelector('[data-account-vehicle-make]')
    const selMo = root?.querySelector('[data-account-vehicle-model]')
    const vehicle = {
      year: selY?.value?.trim() || '',
      make: selM?.value?.trim() || '',
      model: selMo?.value?.trim() || '',
    }
    if (!vehicle.year || !vehicle.make || !vehicle.model) {
      setAccountStatus(str().account_error || 'Complete all fields.', true)
      return
    }
    setVehicle(vehicle)
    if (cfg().customerLoggedIn) {
      try {
        await postProxy('/save', vehicle)
        setAccountStatus(str().account_saved || 'Saved', false)
      } catch {
        setAccountStatus(str().account_error || 'Error', true)
      }
    } else {
      setAccountStatus(str().account_saved || 'Saved', false)
    }
    updateCollectionBar()
    refreshProductBadges()
  }

  function bindAccountForm() {
    const root = document.querySelector('[data-vehicle-fit-account]')
    if (!root || !cfg().enabled) return
    root.querySelector('[data-account-vehicle-year]')?.addEventListener('change', () => onAccountYearChange())
    root.querySelector('[data-account-vehicle-make]')?.addEventListener('change', () => onAccountMakeChange())
    root.querySelector('[data-account-vehicle-save]')?.addEventListener('click', () => saveAccountVehicle())
    root.querySelector('[data-account-vehicle-clear]')?.addEventListener('click', async () => {
      await clearVehicleEverywhere()
      setAccountStatus('', false)
      hydrateAccountSelects()
    })
    hydrateAccountSelects()
  }

  function init() {
    if (!cfg().enabled) return
    setAccentColor()
    mergeInitialVehicle()
    bindModal()
    bindTriggers()
    bindCollectionBar()
    bindAccountForm()
    updateCollectionBar()
    updateHeaderButton()
    // Lit renders shadow DOMs asynchronously; defer one macrotask so .product-card__media exists
    setTimeout(refreshProductBadges, 0)
    window.addEventListener(EVENT_CHANGED, () => {
      updateCollectionBar()
      updateHeaderButton()
      refreshProductBadges()
    })
    document.addEventListener(EVENT_GRID, () => {
      refreshProductBadges()
    })
  }

  window.OrpVehicleFit = {
    getVehicle,
    setVehicle,
    clearVehicle: clearVehicleEverywhere,
    openModal,
    closeModal,
    refreshProductBadges,
    removeVehicleFitBadgesFromDom,
    productFitsVehicle,
    loadYmmLevel,
    EVENT_CHANGED,
    EVENT_GRID,
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }
})()
